import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { ArrowRight, ArrowLeft, CheckCircle, Package, Phone, MessageCircle } from 'lucide-react';
import Autoplay from 'embla-carousel-autoplay';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useInView(ref) {
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.1 });
    o.observe(ref.current);
    return () => o.disconnect();
  }, [ref]);
  return v;
}
function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref);
  return <div ref={ref} className={`${className} transition-all duration-700 ${v ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

function makeSlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function ProductDetail() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/products/${slug}`).catch(() => null),
      axios.get(`${API}/products`),
      axios.get(`${API}/site-content`),
    ]).then(([prodRes, allRes, scRes]) => {
      let found = prodRes?.data;
      // If not found by id/slug from backend, try local fallback
      if (!found && allRes?.data) {
        found = allRes.data.find(p => makeSlug(p.name) === slug || p.id === slug);
      }
      setProduct(found || null);
      setAllProducts((allRes?.data || []).filter(p => p.id !== (found?.id || slug)));
      setSiteContent(scRes?.data || {});
    }).catch(() => {}).finally(() => setLoading(false));
    axios.post(`${API}/analytics/visit`, { page: `/products/${slug}` }).catch(() => {});
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-muted-foreground animate-fade-in">Loading...</div>;
  if (!product) return <div className="py-32 text-center text-muted-foreground">Product not found</div>;

  const specs = product.specifications || [];
  const whatsapp = siteContent.whatsapp_number || '15485322279';
  const phone = siteContent.phone || '+1 548-532-2279';

  // Build a polite, detailed WhatsApp enquiry message for this specific product
  const buildWhatsAppMessage = () => {
    const brand = specs.find(s => /brand/i.test(s.key))?.value || product.name;
    const dose = specs.find(s => /dose|composition|strength/i.test(s.key))?.value;
    const pack = specs.find(s => /packaging size|pack/i.test(s.key))?.value;
    const productUrl = typeof window !== 'undefined' ? window.location.href : '';

    const lines = [
      `Hello Dr. Reenu Women's Clinic,`,
      ``,
      `I would like to enquire about the following product:`,
      ``,
      `• Product: ${product.name}`,
      brand && brand !== product.name ? `• Brand: ${brand}` : null,
      dose ? `• Dose / Composition: ${dose}` : null,
      pack ? `• Pack: ${pack}` : null,
      product.price ? `• Listed price: ${product.price}` : null,
      ``,
      `Product link: ${productUrl}`,
      ``,
      `Could you please share the current price, availability and delivery options? I would also like to know about the consultation process.`,
      ``,
      `Thank you.`
    ].filter(Boolean);
    return lines.join('\n');
  };
  const whatsappMessage = encodeURIComponent(buildWhatsAppMessage());
  const whatsappLink = `https://wa.me/${whatsapp}?text=${whatsappMessage}`;

  const imageAlt = product.image_alt || product.name;

  return (
    <div data-testid="product-detail-page">
      <div className="py-8 sm:py-14">
        <div className="container mx-auto px-5 sm:px-8">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors group" data-testid="back-to-products">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back to Products
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
            {/* Image */}
            <Reveal>
              <div className="rounded-2xl overflow-hidden bg-muted border border-border hover-float sticky top-24">
                <img src={product.image_url} alt={imageAlt} className="w-full h-[300px] sm:h-[400px] lg:h-[450px] object-contain bg-white p-4" data-testid="product-image" />
              </div>
            </Reveal>

            {/* Details */}
            <div>
              <Reveal delay={100}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground">{product.category}</span>
                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light mt-3" data-testid="product-name">{product.name}</h1>
                  </div>
                  <a href={`tel:${phone}`} className="hidden sm:block" data-testid="call-for-deal-btn">
                    <Button className="rounded-full px-5 bg-primary pulse-glow whitespace-nowrap text-sm">
                      <Phone className="w-4 h-4 mr-1.5" /> Call for Best Deal
                    </Button>
                  </a>
                </div>
                <div className="accent-line mt-4" />
              </Reveal>

              {/* Specifications Table */}
              {specs.length > 0 && (
                <Reveal delay={200}>
                  <div className="mt-6" data-testid="product-specifications">
                    <h2 className="font-heading text-lg font-medium mb-3">Product Specifications</h2>
                    <div className="border border-border rounded-xl overflow-hidden">
                      {specs.map((spec, i) => (
                        <div key={i} className={`flex ${i % 2 === 0 ? 'bg-muted/30' : 'bg-card'}`} data-testid={`spec-row-${i}`}>
                          <div className="w-[38%] sm:w-[35%] p-2.5 sm:p-3 border-r border-border">
                            <span className="text-xs sm:text-sm font-medium text-foreground/80">{spec.key}</span>
                          </div>
                          <div className="flex-1 p-2.5 sm:p-3">
                            <span className="text-xs sm:text-sm text-muted-foreground">{spec.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}

              {/* Description */}
              <Reveal delay={300}>
                <div className="mt-6" data-testid="product-description">
                  <h2 className="font-heading text-lg font-medium mb-2">About This Product</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{product.description}</p>
                </div>
              </Reveal>

              {/* Additional Details — clean card layout */}
              {product.details && (
                <Reveal delay={350}>
                  <div className="mt-5 p-4 rounded-xl bg-muted/30 border border-border" data-testid="additional-details">
                    <h3 className="text-sm font-heading font-medium mb-1.5">Important Information</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{product.details}</p>
                  </div>
                </Reveal>
              )}

              {/* Packaging */}
              {product.packaging_details && (
                <Reveal delay={380}>
                  <div className="mt-4 p-4 rounded-xl bg-muted/20 border border-border/60" data-testid="packaging-details">
                    <h3 className="text-sm font-heading font-medium mb-1.5">Packaging & Shipping</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{product.packaging_details}</p>
                  </div>
                </Reveal>
              )}

              {/* Status */}
              <Reveal delay={400}>
                <div className="mt-5 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 ${product.in_stock ? 'text-primary' : 'text-destructive'}`} />
                    <span className="text-sm font-medium">{product.in_stock ? t('products.inStock') : t('products.outOfStock')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{product.price || t('products.contactPricing')}</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Mobile CTA */}
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border p-3 flex gap-3 sm:hidden" data-testid="product-cta-bar">
            <a href={`tel:${phone}`} className="flex-1">
              <Button variant="outline" className="w-full h-11 rounded-xl text-sm">
                <Phone className="w-4 h-4 mr-2" /> Call Now
              </Button>
            </a>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button className="w-full h-11 rounded-xl text-sm bg-[#25D366] hover:bg-[#20bd5a]">
                <MessageCircle className="w-4 h-4 mr-2" /> Get Best Price
              </Button>
            </a>
          </div>

          {/* Desktop CTA */}
          <Reveal>
            <div className="hidden sm:flex gap-4 mt-8 p-5 bg-card border border-border rounded-2xl items-center justify-between" data-testid="product-cta-desktop">
              <div>
                <p className="font-heading text-base font-medium">Interested in {product.name}?</p>
                <p className="text-sm text-muted-foreground mt-0.5">Contact us for pricing, availability, and discreet delivery options.</p>
              </div>
              <div className="flex gap-3">
                <a href={`tel:${phone}`}>
                  <Button variant="outline" className="rounded-full px-5 text-sm"><Phone className="w-4 h-4 mr-1.5" /> Call Now</Button>
                </a>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-full px-5 text-sm bg-[#25D366] hover:bg-[#20bd5a]"><MessageCircle className="w-4 h-4 mr-1.5" /> Get Best Price</Button>
                </a>
              </div>
            </div>
          </Reveal>

          {/* Suggested Products */}
          {allProducts.length > 0 && (
            <Reveal className="mt-14 sm:mt-20 mb-16 sm:mb-0">
              <div data-testid="suggested-products-section">
                <h2 className="font-heading text-xl sm:text-2xl tracking-tight font-normal mb-2">{t('products.suggestedTitle')}</h2>
                <div className="accent-line mb-6" />
                <Carousel opts={{ loop: true, align: 'start' }} plugins={[Autoplay({ delay: 3500, stopOnInteraction: true })]} className="w-full">
                  <CarouselContent className="-ml-4">
                    {allProducts.map((p, i) => (
                      <CarouselItem key={p.id} className="pl-4 basis-[75%] sm:basis-1/2 lg:basis-1/3">
                        <Link to={`/products/${makeSlug(p.name)}`} className="block h-full" data-testid={`suggested-product-link-${i}`}>
                          <Card className="hover-float overflow-hidden border border-border rounded-2xl group card-shimmer relative h-full cursor-pointer" data-testid={`suggested-product-${i}`}>
                            <div className="aspect-[4/3] overflow-hidden bg-white">
                              <img src={p.image_url} alt={p.image_alt || p.name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" />
                            </div>
                            <CardContent className="p-4">
                              <h3 className="font-heading text-base font-medium group-hover:text-primary transition-colors">{p.name}</h3>
                              <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{p.short_description}</p>
                              <span className="inline-flex items-center mt-2 text-xs font-medium text-primary" data-testid={`suggested-view-${i}`}>
                                {t('products.viewDetails')} <ArrowRight className="ml-1 w-3 h-3" />
                              </span>
                            </CardContent>
                          </Card>
                        </Link>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </div>
  );
}
